import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import PageWrapper from "../../components/UI/PageWrapper/PageWrapper";
import { Block, NewBlockButton } from "./Block";
import BlockContent from "./BlockContent";
import {
  exportDocuments,
  getAllDocumentations,
  importDocuments,
} from "../../store/Documentation/actions";
import "./Documentation.scss";

const Documentation = () => {
  const [activeTab, setActiveTab] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [errors, setErrors] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const navigate = useNavigate();
  const { noteId } = useParams();

  const { documentations = [], documentById, isLoading } = useSelector(
    (state) => state.Documentations || {}
  );

  const dispatch = useDispatch();

  const handleAddNewBlock = () => {
    const uniqueId = uuidv4();
    const newBlock = {
      id: uniqueId,
      header: uniqueId,
      content: "",
    };
    setBlocks([...blocks, newBlock]);
    setActiveTab(uniqueId);
    navigate(`/notes/${uniqueId}`);
  };

  const handleDeleteBlock = (blockId) => {
    setBlocks(
      blocks
        .filter((block) => block.id !== blockId)
        .sort((a, b) => a.header.localeCompare(b.header))
    );
    if (activeTab === blockId) {
      setActiveTab("");
      navigate("/notes");
    }
  };

  const handleBlockSelection = (blockId) => {
    navigate(`/notes/${blockId}`);
  };

  const handleUploadBulkDocuments = (selectedFile) => {
    const formData = new FormData();
    formData.append("file", selectedFile);
    dispatch(importDocuments(formData));
  };

  const resetErrorData = (errors, field) => {
    if (errors?.[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      return newErrors;
    }
    return errors;
  };

  useEffect(() => {
    if (documentations && Array.isArray(documentations)) {
      setBlocks(
        [...documentations].sort((a, b) => a.header.localeCompare(b.header))
      );
    }
  }, [documentations]);

  useEffect(() => {
    const searchLowercase = searchInput.toLowerCase();
    if (documentations && Array.isArray(documentations)) {
      const filtered = documentations.filter((itm) => {
        return (
          itm.header.toLowerCase().includes(searchLowercase) ||
          itm.content.toLowerCase().includes(searchLowercase)
        );
      });
      setBlocks(filtered.sort((a, b) => a.header.localeCompare(b.header)));
    }
  }, [searchInput, documentations]);

  useEffect(() => {
    if (documentById?._id) {
      setBlocks((prev) =>
        prev
          .map((item) => {
            if (item.id === documentById.id) {
              return documentById;
            }
            return item;
          })
          .sort((a, b) => a.header.localeCompare(b.header))
      );
    }
  }, [documentById?._id]);

  useEffect(() => {
    if (noteId) {
      setActiveTab(noteId);
    }
  }, [noteId]);

  useEffect(() => {
    dispatch(getAllDocumentations());
  }, []);

  const permissionList =
    JSON.parse(localStorage.getItem("frontendIds")) || [];
  const IS_ADDABLE = permissionList.includes("description_add");
  const IS_EDITABLE = permissionList.includes("description_edit");
  const IS_DELETABLE = permissionList.includes("description_delete");
  const IS_EXPOTABLE = permissionList.includes("description_export");
  const IS_IMPORTABLE = permissionList.includes("description_import");

  return (
    <PageWrapper>
      <div className="documentation-page">
        {/* Full-screen loading overlay */}
        {isLoading && (
          <div className="documentation-loader-overlay">
            <CircularProgress color="error" />
          </div>
        )}

        {/* Page Header */}
        <div className="documentation-header">
          <h2 className="documentation-title">Notes</h2>

          <div className="documentation-header-actions">
            {/* Search input */}
            <TextField
              size="small"
              type="text"
              variant="outlined"
              placeholder="Search headers, content, tags..."
              value={searchInput}
              onChange={({ target: { value } }) => setSearchInput(value)}
              sx={{ flex: 1, minWidth: 180, backgroundColor: "#fff", borderRadius: "6px" }}
            />

            {/* Upload Bulk (Import) */}
            {IS_IMPORTABLE && (
              <div className="upload-file-wrapper">
                <input
                  type="file"
                  accept="application/json"
                  name="coupon-file"
                  id="coupon-file"
                  className="upload-file-input"
                  onChange={({ target: { files } }) => {
                    if (files && files[0]) {
                      handleUploadBulkDocuments(files[0]);
                    }
                  }}
                  onClick={({ target }) => (target.value = "")}
                />
                <label className="upload-file-label" htmlFor="coupon-file">
                  <FileUploadOutlinedIcon fontSize="small" />
                  Upload Bulk
                </label>
              </div>
            )}

            {/* Export All */}
            {IS_EXPOTABLE && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => dispatch(exportDocuments())}
                sx={{
                  backgroundColor: "#2364AA",
                  "&:hover": { backgroundColor: "#1a4f8a" },
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Export All
              </Button>
            )}
          </div>
        </div>

        {/* Main two-column layout */}
        <div className="documentation-grid">
          {/* Left: Tabs/block list */}
          <div className="documentation-tabs-panel">
            {blocks.map((item) => (
              <Block
                key={item?.id}
                link={`/notes/${item.id}`}
                activeTab={activeTab}
                setBlockId={handleBlockSelection}
                blockId={item.id}
                blockName={item.header}
                onDelete={handleDeleteBlock}
              />
            ))}
            {IS_ADDABLE && IS_EDITABLE && (
              <NewBlockButton onClick={handleAddNewBlock} />
            )}
          </div>

          {/* Right: Editor / Viewer */}
          <div className="documentation-content-panel">
            <BlockContent
              blockData={blocks.find((itm) => itm?.id === activeTab)}
              blockId={activeTab}
              isEditable={IS_EDITABLE}
              isDeletable={IS_DELETABLE}
              errors={errors}
              setErrors={setErrors}
              resetErrorData={resetErrorData}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default Documentation;
